import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ClipboardCheck, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Lists this coach's UPCOMING assessment bookings (status = scheduled).
 * Each row has a one-click "Mark attended" button that flips the player's
 * assessment_status to completed and unlocks Performance/1:1 bookings.
 */
export default function PendingAssessmentsCard() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = async () => {
    try {
      const { data } = await api.get("/coach/assessments?status_filter=scheduled&mine=1");
      setBookings(data || []);
    } catch (e) {
      // soft fail — keep widget hidden if endpoint errors
      setBookings([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return null;
  if (!bookings.length) return null;

  const markAttended = async (b) => {
    setBusy((cur) => ({ ...cur, [b.id]: true }));
    try {
      await api.post(`/coach/assessments/${b.id}/attended`);
      toast.success(`${b.student?.name || "Player"} marked attended — feedback pending`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy((cur) => ({ ...cur, [b.id]: false }));
    }
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("en-GB", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  return (
    <div className="card-surface p-4 sm:p-5 border-l-4 border-[#FF4500] fade-up d2" data-testid="pending-assessments-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="label-mono inline-flex items-center gap-2" style={{ color: "#FF4500" }}>
          <ClipboardCheck className="w-3.5 h-3.5" /> PENDING ASSESSMENTS · {bookings.length}
        </div>
        <Link to="/calendar" className="text-xs text-[#A3A3A3] hover:text-[#FF4500] uppercase tracking-wider inline-flex items-center gap-1">
          Calendar <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
      <div className="space-y-2">
        {bookings.map((b) => (
          <div key={b.id} className="grid sm:grid-cols-12 items-center gap-3 px-3 py-3 -mx-3 hover:bg-[#141414] transition-colors" data-testid={`pending-assessment-${b.id}`}>
            <div className="sm:col-span-5 min-w-0">
              <Link to={`/players/${b.student?.id || b.student_id}`} className="text-sm font-semibold text-white hover:text-[#FF4500] truncate inline-block max-w-full" data-testid={`pending-assessment-player-${b.id}`}>
                {b.student?.name || "Player"}
              </Link>
              <div className="text-[10px] text-[#525252] truncate">{b.student?.email}</div>
            </div>
            <div className="sm:col-span-4 text-xs text-[#A3A3A3] min-w-0 truncate">{b.offering?.venue || "—"}</div>
            <div className="sm:col-span-3 flex items-center justify-end gap-2 flex-wrap">
              <div className="text-[10px] text-[#525252] uppercase tracking-wider">{fmt(b.start || b.offering?.scheduled_at)}</div>
              <button
                type="button"
                disabled={busy[b.id]}
                onClick={() => markAttended(b)}
                data-testid={`mark-attended-${b.id}`}
                className="px-3 py-2 text-[10px] uppercase tracking-wider font-bold inline-flex items-center gap-1 disabled:opacity-50"
                style={{ background: "#FF4500", color: "#0A0A0A" }}
              >
                <Check className="w-3 h-3" /> {busy[b.id] ? "Marking…" : "Mark attended"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
